import { CircleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type DeleteArticlesDialogParams = {
  articleIds: string[];
  articleTitle?: string;
};

type Props = {
  isLoading: boolean;
  onConfirm: (articleIds: string[]) => void;
};

export const DeleteArticlesDialog = ({ isLoading, onConfirm }: Props) => {
  const { t } = useTranslation('knowledge_base');
  const { isOpen, closeModal, getParams } = useModalStore();

  const open = isOpen(MODAL_NAMES.DELETE_ARTICLES_DIALOG);
  const params = open ? (getParams() as DeleteArticlesDialogParams | null) : null;

  const articleIds = params?.articleIds ?? [];
  const articleTitle = params?.articleTitle;
  const count = articleIds.length;

  const title = useMemo(
    () =>
      count > 1
        ? t('delete_confirmation_modal.title_plural', { count })
        : t('delete_confirmation_modal.title_single'),
    [count, t],
  );

  const description = useMemo(
    () =>
      count > 1
        ? t('delete_confirmation_modal.description_plural', { count })
        : t('delete_confirmation_modal.description_single', {
            title: articleTitle ?? t('delete_confirmation_modal.default_title'),
          }),
    [articleTitle, count, t],
  );

  const handleRequestClose = () => {
    if (isLoading) {
      return;
    }
    closeModal();
  };

  const handleConfirm = () => {
    if (count === 0 || isLoading) {
      return;
    }

    onConfirm(articleIds);
  };

  if (!open) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={(value) => !value && handleRequestClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <CircleAlert className="size-4 bg-red-100 rounded-full text-red-500 p-1.5 box-content" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel asChild disabled={isLoading}>
            <Button variant="secondary" size="sm" disabled={isLoading}>
              {t('button.cancel')}
            </Button>
          </AlertDialogCancel>
          <Button
            variant="danger"
            size="sm"
            disabled={isLoading || count === 0}
            onClick={handleConfirm}
          >
            {isLoading && <Spinner className="text-white h-4" />}
            {count > 1 ? t('button.permanently_delete') : t('button.delete')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
